import React, { useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, Search, Filter, CheckCircle, XCircle, Clock, DollarSign, RefreshCw, Eye, Send } from 'lucide-react';
import { toast } from 'sonner';

const ClaimsManagementProPage = () => {
  const [claims, setClaims] = useState([
    { id: '1', claimNumber: 'CLM-2026-001234', patient: 'Smith, John', payer: 'Medicare', dos: '01/20/2026', amount: '$1,250.00', status: 'Paid', paidAmount: '$1,125.00', paidDate: '01/25/2026' },
    { id: '2', claimNumber: 'CLM-2026-001235', patient: 'Johnson, Mary', payer: 'UnitedHealthcare', dos: '01/19/2026', amount: '$890.00', status: 'Pending', paidAmount: '-', paidDate: '-' },
    { id: '3', claimNumber: 'CLM-2026-001236', patient: 'Williams, Robert', payer: 'Aetna', dos: '01/18/2026', amount: '$1,540.00', status: 'Denied', paidAmount: '$0.00', paidDate: '01/24/2026' },
    { id: '4', claimNumber: 'CLM-2026-001237', patient: 'Brown, Patricia', payer: 'Medicare', dos: '01/17/2026', amount: '$2,100.00', status: 'In Review', paidAmount: '-', paidDate: '-' },
    { id: '5', claimNumber: 'CLM-2026-001238', patient: 'Davis, Michael', payer: 'BCBS', dos: '01/16/2026', amount: '$675.00', status: 'Paid', paidAmount: '$675.00', paidDate: '01/23/2026' },
    { id: '6', claimNumber: 'CLM-2026-001239', patient: 'Garcia, Jennifer', payer: 'Humana', dos: '01/15/2026', amount: '$1,890.00', status: 'Submitted', paidAmount: '-', paidDate: '-' },
  ]);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Paid':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Paid</span>;
      case 'Denied':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium flex items-center gap-1"><XCircle className="w-3 h-3" /> Denied</span>;
      case 'Pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>;
      case 'In Review':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium flex items-center gap-1"><RefreshCw className="w-3 h-3" /> In Review</span>;
      case 'Submitted':
        return <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium flex items-center gap-1"><Send className="w-3 h-3" /> Submitted</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  const filteredClaims = claims.filter(claim => {
    const matchesSearch = claim.patient.toLowerCase().includes(searchQuery.toLowerCase()) || claim.claimNumber.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || claim.status.toLowerCase().replace(' ', '') === filterStatus.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: claims.length,
    paid: claims.filter(c => c.status === 'Paid').length,
    pending: claims.filter(c => c.status === 'Pending' || c.status === 'Submitted' || c.status === 'In Review').length,
    denied: claims.filter(c => c.status === 'Denied').length,
    totalBilled: claims.reduce((sum, c) => sum + parseFloat(c.amount.replace(/[$,]/g, '')), 0),
    totalPaid: claims.filter(c => c.paidAmount !== '-').reduce((sum, c) => sum + parseFloat(c.paidAmount.replace(/[$,]/g, '')), 0)
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="claims-management-pro-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Claims Management Pro</h1>
            <p className="text-gray-600">Inovalon Claim Portal - Professional Claims</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-500">Total Claims</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.paid}</p>
            <p className="text-sm text-gray-500">Paid</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            <p className="text-sm text-gray-500">Pending</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.denied}</p>
            <p className="text-sm text-gray-500">Denied</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-xl font-bold text-gray-900">${stats.totalBilled.toLocaleString()}</p>
            <p className="text-sm text-gray-500">Total Billed</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-xl font-bold text-green-600">${stats.totalPaid.toLocaleString()}</p>
            <p className="text-sm text-gray-500">Total Paid</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by patient or claim #..." className="pl-10" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Status:</Label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
                <option value="all">All</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="submitted">Submitted</option>
                <option value="inreview">In Review</option>
                <option value="denied">Denied</option>
              </select>
            </div>
            <Button variant="outline" size="sm"><Filter className="w-4 h-4 mr-2" /> More Filters</Button>
          </div>
        </div>

        {/* Claims Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Claim #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Payer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">DOS</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Billed</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Paid</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Paid Date</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredClaims.map((claim) => (
                  <tr key={claim.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-blue-600 font-medium">{claim.claimNumber}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{claim.patient}</td>
                    <td className="px-4 py-3 text-gray-600">{claim.payer}</td>
                    <td className="px-4 py-3 text-gray-600">{claim.dos}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{claim.amount}</td>
                    <td className="px-4 py-3 text-center">{getStatusBadge(claim.status)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-green-600">{claim.paidAmount}</td>
                    <td className="px-4 py-3 text-gray-500">{claim.paidDate}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => toast.info(`Viewing claim ${claim.claimNumber}`)}>
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ClaimsManagementProPage;
